import { Controller } from "@hotwired/stimulus"

// Drives the 3-way deposit mode control (per person / per reservation / disable)
// on the restaurant deposit settings form. Switching the segmented control (or
// editing the fee / "parties larger than" value) live-updates the fee label,
// placeholder, and an explainer line with a worked example. Selecting "Disable"
// hides the configuration fields (they still submit so the config is preserved).
export default class extends Controller {
  static targets = ["feeLabel", "feeInput", "explainer", "partyLimit", "config"]

  connect() {
    this.update()
  }

  update() {
    const mode = this.mode()
    if (mode === "disabled") {
      this.applyDisabled()
      return
    }

    this.showConfig()
    if (mode === "per_reservation") {
      this.applyPerReservation()
    } else {
      this.applyPerPerson()
    }
  }

  applyDisabled() {
    if (this.hasConfigTarget) this.configTarget.classList.add("d-none")
  }

  showConfig() {
    if (this.hasConfigTarget) this.configTarget.classList.remove("d-none")
  }

  applyPerPerson() {
    this.feeLabelTarget.textContent = "Per Person Fee"
    this.feeInputTarget.placeholder = "Deposit per person"

    const example = this.examplePartySize()
    const fee = this.feeAmount()
    let text = "Each guest is charged this amount."
    if (fee > 0) {
      text += ` A party of ${example} pays ${example} × ${this.money(fee)} = ${this.money(fee * example)}.`
    }
    this.explainerTarget.textContent = text
  }

  applyPerReservation() {
    this.feeLabelTarget.textContent = "Per Reservation Deposit"
    this.feeInputTarget.placeholder = "Total deposit for the party"

    const fee = this.feeAmount()
    const amount = fee > 0 ? this.money(fee) : "this amount"
    this.explainerTarget.textContent = `The whole party pays ${amount} once, regardless of size.`
  }

  mode() {
    const checked = this.element.querySelector("input[name='paid_table[deposit_mode]']:checked")
    // Default to per-person when nothing is checked yet.
    return checked ? checked.value : "per_person"
  }

  feeAmount() {
    const value = parseFloat(this.feeInputTarget.value)
    return isNaN(value) ? 0 : value
  }

  // Smallest party that triggers the deposit: parties of N or more -> N.
  examplePartySize() {
    const limit = this.hasPartyLimitTarget ? parseInt(this.partyLimitTarget.value, 10) : NaN
    return isNaN(limit) ? 2 : limit
  }

  money(amount) {
    // Drop the cents when the value is whole ($20 instead of $20.00).
    const rounded = Math.round(amount * 100) / 100
    return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`
  }
}
